import { useEffect, useRef } from "react";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Play, ExternalLink } from "lucide-react";
import TopNav from "@/components/TopNav";
import StepProgressBar from "@/components/StepProgressBar";
import { steps, getStepByNo, TOTAL_STEPS } from "@/data/lampContent";

const FIRST_BUILD_STEP = steps[0].no; // 2
const LAST_BUILD_STEP = steps[steps.length - 1].no; // 14

export default function StepPage() {
  const { no } = useParams();
  const navigate = useNavigate();
  const stepNo = Number(no);
  const step = getStepByNo(stepNo);
  const videoRef = useRef(null);

  // Reload the video when navigating between step pages (same <video> node
  // may otherwise keep the previous src cached and not restart cleanly).
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.load();
      const p = videoRef.current.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    }
  }, [stepNo]);

  if (!step) {
    return <Navigate to="/checklist" replace />;
  }

  const prevTo = stepNo === FIRST_BUILD_STEP ? "/checklist" : `/step/${stepNo - 1}`;
  const nextTo = stepNo === LAST_BUILD_STEP ? "/done" : `/step/${stepNo + 1}`;

  return (
    <div
      className="min-h-screen flex flex-col bg-[var(--bg-base)]"
      data-testid="step-page"
      data-step-no={stepNo}
    >
      <TopNav />
      <StepProgressBar activeStep={stepNo} />

      <main className="flex-1 w-full mx-auto max-w-3xl px-4 sm:px-6 pt-6 pb-32">
        <motion.article
          key={stepNo}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="card-brut bg-white overflow-hidden"
          data-testid={`step-card-${stepNo}`}
        >
          {/* Header row: badge + title */}
          <div className="flex items-start gap-4 p-5 sm:p-6 border-b-[3px] border-[var(--border-heavy)]">
            <span
              className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full border-[3px] border-[var(--border-heavy)] bg-[var(--primary)] font-heading text-xl font-bold text-[var(--text-main)] shadow-[3px_3px_0px_var(--border-heavy)]"
              data-testid={`step-badge-${stepNo}`}
            >
              {stepNo}
            </span>
            <h1
              className="pt-1 font-heading text-xl sm:text-2xl font-semibold leading-snug text-[var(--text-main)]"
              data-testid={`step-title-${stepNo}`}
            >
              {step.title}
            </h1>
          </div>

          {/* Video (if provided) */}
          {step.video && (
            <div
              className="relative w-full aspect-video bg-black border-b-[3px] border-[var(--border-heavy)] overflow-hidden"
              data-testid={`step-media-${stepNo}`}
            >
              <video
                ref={videoRef}
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                className="w-full h-full object-contain"
                data-testid={`step-video-${stepNo}`}
              >
                <source src={step.video} type="video/mp4" />
                {step.videoWebm && (
                  <source src={step.videoWebm} type="video/webm" />
                )}
              </video>
            </div>
          )}

          {/* Substeps */}
          {step.substeps && step.substeps.length > 0 && (
            <ol
              className="p-5 sm:p-6 flex flex-col gap-3"
              data-testid={`step-substeps-${stepNo}`}
            >
              {step.substeps.map((sub, idx) => (
                <li
                  key={idx}
                  className="flex items-start gap-3"
                  data-testid={`step-${stepNo}-sub-${idx + 1}`}
                >
                  <span className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border-2 border-[var(--border-heavy)] bg-white font-heading text-sm font-bold text-[var(--text-main)]">
                    {String.fromCharCode(97 + idx)}
                  </span>
                  <p className="text-base sm:text-lg text-[var(--text-main)] leading-relaxed">
                    {sub}
                  </p>
                </li>
              ))}
            </ol>
          )}

          {/* Watch this step button — sits below the text */}
          {step.yt && (
            <div className="px-5 sm:px-6 pb-5 sm:pb-6">
              <a
                href={step.yt}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`step-yt-btn-${stepNo}`}
                className="press-btn inline-flex items-center gap-2 rounded-full border-2 border-[var(--border-heavy)] bg-white text-[var(--text-main)] px-4 py-2 font-heading text-sm font-semibold shadow-[3px_3px_0px_var(--border-heavy)] hover:bg-[var(--bg-paper)] transition-colors"
              >
                <Play
                  strokeWidth={2.5}
                  fill="currentColor"
                  className="h-4 w-4 text-[var(--accent)]"
                />
                Watch this step
                <ExternalLink
                  strokeWidth={3}
                  className="h-3.5 w-3.5 text-[var(--accent)]"
                />
              </a>
            </div>
          )}
        </motion.article>
      </main>

      {/* Bottom nav */}
      <div
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 flex items-center gap-6"
        data-testid="step-bottom-nav"
      >
        <NavArrow
          direction="prev"
          onClick={() => navigate(prevTo)}
          testId={`step-${stepNo}-prev-btn`}
          ariaLabel={`Back to ${prevTo === "/checklist" ? "checklist" : `step ${stepNo - 1}`}`}
        />
        {stepNo === LAST_BUILD_STEP ? (
          <button
            type="button"
            onClick={() => navigate(nextTo)}
            aria-label="Next"
            data-testid={`step-${stepNo}-next-btn`}
            className="press-btn inline-flex items-center gap-2 rounded-full bg-white border-2 border-[var(--border-heavy)] px-5 py-3 font-heading text-sm font-semibold text-[var(--text-main)] shadow-[3px_3px_0px_var(--border-heavy)] hover:bg-[var(--bg-paper)]"
          >
            Next
            <ChevronRight strokeWidth={3} className="h-5 w-5 text-[#d64533]" />
          </button>
        ) : (
          <NavArrow
            direction="next"
            onClick={() => navigate(nextTo)}
            testId={`step-${stepNo}-next-btn`}
            ariaLabel={`Continue to step ${stepNo + 1}`}
          />
        )}
      </div>
    </div>
  );
}

function NavArrow({ direction, onClick, testId, ariaLabel }) {
  const Icon = direction === "prev" ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      data-testid={testId}
      className="arcade-btn arcade-btn-icon"
    >
      <Icon strokeWidth={3} className="h-6 w-6" />
    </button>
  );
}
